import { useTranslation } from 'react-i18next';
import IconButton from '@common/components/controls/IconButton';
import { rotateIcon } from '@common/components/Icon';
import Menu from '@common/components/Menu';
import MenuActionOption from '@common/components/Menu/MenuActionOption';
import MenuNavigationOption from '@common/components/Menu/MenuNavigationOption';
import ColorThemesMenu from '@common/components/PageHeader/PageHeaderMenu/ColorThemesMenu';
import IconArrowTriangleDown from '@common/components/svgs/common/ArrowTriangleDown';
import IconFlag from '@common/components/svgs/common/Flag';
import IconLogout from '@common/components/svgs/common/Logout';
import IconSettings from '@common/components/svgs/common/Settings';
import { retailerLevelNamesMap } from '@common/types/api';
import type User from '@common/types/models/User';
import { getUserLevelNameKey } from '@common/types/models/User';
import { getAccessRights } from '@common/utils/accessRights';

interface AuthenticatedDesktopMenuProps {
  user: User;
  logout: () => void;
  mustAcceptEula: boolean | undefined;
  showBetaFeatures?: boolean;
}

function AuthenticatedDesktopMenu({
  user,
  logout,
  mustAcceptEula,
  showBetaFeatures,
}: Readonly<AuthenticatedDesktopMenuProps>) {
  const { t } = useTranslation();
  // No feature flags here (the menu also renders without a store): read only rights that ignore flags
  const { isCustomer, isPartner } = getAccessRights(user, {}, APP_TYPE);
  return (
    <Menu
      align="right"
      data-testid="desktopMenu"
      renderSelector={(selectorRef, toggleMenu, isOpen) => (
        <div ref={selectorRef} className="align-items-center">
          <span className="menu__leading-icon">
            <span className="page-header__user-initials">
              {user.initials()}
            </span>
          </span>
          <span style={{ display: 'inline-flex' }}>
            <span className="flex-column">
              {!isCustomer
                && (
                  <span className="page-header__user-level">
                    {typeof user.retailerLevel === 'number'
                        && isPartner
                      ? t(retailerLevelNamesMap[user.retailerLevel])
                      : t(getUserLevelNameKey(user.userLevel))}
                  </span>
                )}
              <span className="page-header__username">
                {user.fullName()}
              </span>
            </span>
            <IconButton
              onClick={toggleMenu}
              icon={isOpen ? rotateIcon(IconArrowTriangleDown, 180) : IconArrowTriangleDown}
              data-testid="openDesktopMenuButton"
            />
          </span>
        </div>
      )}
    >
      {mustAcceptEula === false && (
        <>
          <MenuNavigationOption
            id="menu.option.settings"
            leadingIcon={IconSettings}
            to={{ pathname: '/account' }}
            data-testid="showAccount-menunavigation"
          />
          {showBetaFeatures && (
            <MenuNavigationOption
              id="menu.option.beta-features"
              leadingIcon={IconFlag}
              to={{ pathname: '/beta-features' }}
              data-testid="showBetaFeatures-menunavigation"
            />
          )}
        </>
      )}
      <ColorThemesMenu />
      <MenuActionOption
        id="page-header.buttons.logout"
        data-testid="logoutButton"
        leadingIcon={IconLogout}
        onClick={logout}
      />
    </Menu>
  );
}

export default AuthenticatedDesktopMenu;
